import { MRT_ColumnDef, MRT_RowData } from 'material-react-table';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ROUTER_PATHS,
  TABLE_CONSTANTS_COLUMS,
  emptyCellOneLevel,
  formatDateWithTimeCell,
  hasAllPermissions,
  hasPermission,
} from '@/shared';

import {
  CustomSearch,
  GeneralTable,
  SampleTable,
  SingleButton,
  SingleTableBoxScene,
  SwitchComponente,
} from '@/shared/components/ui';
import {
  useCheckPermissions,
  useTableFilter,
  useTableServerSideFiltering,
} from '@/shared/hooks';
import { useUiConfirmModalStore } from '@/store/ui';
import { useFetchGastos, useUpdateGasto } from '@/shared/actions/erp/inventario/gastos.actions';
import { Gastos } from '@/shared/interfaces/erp/inventario';
import PaymentIcon from '@mui/icons-material/Payment';
import { TreeNode } from '@/shared/interfaces';
import { ScrollableDialogProps } from '@/shared/components/ui/Dialogs';
import { Grid } from '@mui/material';
import { PermissionsEnum } from '@/auth/interfaces';

export type GastosPageProps = {};
export const returnUrlGastosPage = ROUTER_PATHS.inventario.gastos_nav

const GastosPage: React.FC<GastosPageProps> = () => {
  useCheckPermissions(PermissionsEnum.api_view_gasto)


  const navigate = useNavigate();

  // server side filters - colums table
  const { filterObject, columnFilters, setColumnFilters } =
    useTableServerSideFiltering();

  ///* global state
  const setConfirmDialog = useUiConfirmModalStore(s => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore(
    s => s.setConfirmDialogIsOpen
  );

  const [details, setDetails] = useState<TreeNode[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  ///* table
  const {
    globalFilter,
    pagination,
    searchTerm,
    onChangeFilter,
    setPagination,
  } = useTableFilter();
  const { pageIndex, pageSize } = pagination;

  ///* fetch data
  const {
    data: GastosPagingRes,
    isLoading,
    isRefetching,
  } = useFetchGastos({
    page: pageIndex + 1,
    page_size: pageSize,
    nombre: searchTerm,
    ...filterObject,
    filterByState: false,
  });

  ///* mutations
  const updSate = useUpdateGasto({
    enableRedirect: false,
  });
  const onDetails = (e: MRT_RowData) => {
    setDetails(JSON.parse(e.cuentas_contables));
    setIsModalOpen(true);
  }
  ///* columns
  const columnsPlan = useMemo<MRT_ColumnDef<TreeNode>[]>(
    () => [
      {
        accessorKey: 'id', //access nested data with dot notation
        header: 'Numero Cuenta',
        size: 150
      },

      {
        accessorKey: 'name',
        header: 'Nombre de la cuenta',
        size: 500,
      },
    ],
    [],
  );
  const columns = useMemo<MRT_ColumnDef<Gastos>[]>(
    () => [
      {
        accessorKey: 'numero_referencia', // to allow filtering
        header: 'Número de Referencia',
        size: 200,
        Cell: ({ row }) => row.original?.numero_referencia || 'N/A',
      },
      {
        accessorKey: 'costo',
        header: 'Costo',
        size: 200,
        Cell: ({ row }) => emptyCellOneLevel(row, 'costo'),
      },
      {
        accessorKey: 'descripcion',
        header: 'Descripción',
        size: 200,
        Cell: ({ row }) => emptyCellOneLevel(row, 'descripcion'),
      },
      {
        accessorKey: 'iva',
        header: 'IVA',
        size: 200,
        Cell: ({ row }) => emptyCellOneLevel(row, 'iva'),
      },
      {
        accessorKey: 'ice',
        header: 'ICE',
        size: 200,
        Cell: ({ row }) => emptyCellOneLevel(row, 'iva'),
      },
      {
        accessorKey: 'nombre',
        header: 'Nombre',
        size: 270,
        Cell: ({ row }) => emptyCellOneLevel(row, 'nombre'),
      },

      {
        accessorKey: 'estado',
        header: 'estado',
        size: 200,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          return (
            <SwitchComponente
              estadoSwitch={row.original?.estado!}
              titulo={row.original.estado ? 'SI' : 'NO'}
              setEstadoSwitch={value => {
                if (!hasPermission(PermissionsEnum.api_change_gasto)) return;
                setConfirmDialog({
                  isOpen: true,
                  title: 'Cambiar de estado de Gastos',
                  subtitle:
                    '¿Está seguro que desea cambiar el estado de este registro?',
                  onConfirm: () => {
                    setConfirmDialogIsOpen(false);
                    updSate.mutate({
                      id: row.original?.id_gasto!,
                      data: { Gastos: !!value } as any,
                    });
                  },
                });
              }}
            />
          );
        },
      },
      {
        accessorKey: 'cuentas_contables',
        header: 'Cuentas contables',
        size: 200,
        Cell: ({ row }) => {
          return (
            <>
              <SingleButton
                label="ver"
                variant="text"
                color="info"
                onClick={() => {
                  onDetails(row.original);
                }}
                startIcon={<PaymentIcon />}
              />
            </>
          )
        },
      },
      {
        accessorKey: 'fecha_creacion',
        header: 'Fecha de Creación',
        size: 200,
        enableColumnFilter: true,
        filterVariant: 'date',
        enableSorting: false,
        Cell: ({ row }) => formatDateWithTimeCell(row, 'fecha_creacion'),
      },
      {
        accessorKey: 'fecha_modificacion',
        header: 'Fecha de Modificación',
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => formatDateWithTimeCell(row, 'fecha_modificacion'),
      },
    ],
    []
  );

  ///* handlers
  const onEdit = (row: Gastos) => {
    navigate(`${returnUrlGastosPage}/editar/${row.id_gasto}`);
  };

  return (
    <SingleTableBoxScene
      title="Gastos"
      urlRedirect={`${returnUrlGastosPage}/crear`}
      enableRedirect={hasPermission(PermissionsEnum.api_add_gasto)}
    >
      <CustomSearch
        onChange={onChangeFilter}
        value={globalFilter}
        text="por nombre"
      />

      <GeneralTable<Gastos>
        columns={columns}
        data={GastosPagingRes?.data?.data || []}
        //
        isLoading={isLoading}
        isRefetching={isRefetching}
        // // search
        enableGlobalFilter={false}
        // // filters - llega al back
        enableManualFiltering={true}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        // // pagination
        pagination={pagination}
        onPaging={setPagination}
        rowCount={GastosPagingRes?.data?.count}
        // // actions ========

        actionsColumnSize={TABLE_CONSTANTS_COLUMS.ACTIONCOLUMN_WIDTH}
        // crud
        enableColumnActions={hasAllPermissions([
          PermissionsEnum.api_change_gasto,
        ])}
        onEdit={onEdit}
        canEdit={hasAllPermissions([
          PermissionsEnum.api_change_gasto,
        ])}
        canDelete={false}
      />
      <ScrollableDialogProps
        open={isModalOpen}
        title="Plan cuentas"
        onCloseIcon={()=>setIsModalOpen(false)}
        ///cancelTextBtn="Cerrar"
        //onClose={() => setIsModalOpen(false)}
        minWidth="40%"
        contentNode={
          <>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                {details.length > 0 && (
                  <SampleTable<TreeNode>
                    columns={columnsPlan}
                    data={details}
                    enablePagination={true}
                  />
                )}
              </Grid>
            </Grid>
          </>
        }
      />
    </SingleTableBoxScene>
  );
};

export default GastosPage;